import React, { useState } from 'react';
import { ArrowLeft, Swords, Shuffle, Heart } from 'lucide-react';
import { User } from '../types';

interface BattleModeSelectionProps {
  onSelectMode: (mode: 'FREE' | 'DRAW', health: number) => void;
  onBack: () => void;
}

export const BattleModeSelection: React.FC<BattleModeSelectionProps> = ({ onSelectMode, onBack }) => {
  const [health, setHealth] = useState(3);

  return (
    <div className="h-full w-full flex flex-col items-center justify-start relative overflow-y-auto overflow-x-hidden px-3 py-3 md:p-8">
      <button onClick={onBack} className="absolute top-3 left-3 p-2 text-white/50 hover:text-white transition-colors z-10">
          <ArrowLeft size={24} />
      </button>

      <h1 className="text-[28px] md:text-5xl font-orbitron font-black text-red-500 mb-4 md:mb-6 tracking-widest text-center drop-shadow-[0_0_10px_rgba(239,68,68,0.5)] mt-6 md:mt-8">
          BATTLE MODE
          <br/>
          <span className="text-lg md:text-3xl text-red-500/80">对战模式</span>
      </h1>

      <div className="mb-4 md:mb-8 w-full max-w-3xl rounded-3xl border border-red-500/15 bg-[linear-gradient(135deg,rgba(127,29,29,0.18),rgba(0,0,0,0.12),rgba(127,29,29,0.05))] px-4 py-3 md:px-6 md:py-5 shadow-[0_0_30px_rgba(239,68,68,0.08)]">
        <div className="text-center text-[11px] md:text-sm text-zinc-400 font-rajdhani leading-snug md:leading-relaxed">
          Choose the duel format, set the winning score, and enter the arena with a cleaner battle flow.
          <br />
          选择对战形式与获胜分数，快速进入更清晰的对战流程。
        </div>
      </div>

      {/* Health Configuration */}
      <div className="mb-4 md:mb-10 w-full max-w-sm bg-black/60 border border-zinc-800 rounded-2xl p-4 md:p-6 backdrop-blur-md flex flex-col items-center gap-3 md:gap-4">
        <div className="flex items-center gap-2 text-red-400 font-orbitron tracking-[0.14em] md:tracking-widest uppercase mb-1 md:mb-2 text-xs md:text-base">
          <Heart size={18} className="fill-red-500 text-red-500 animate-pulse shrink-0" />
          <span className="whitespace-nowrap">Winning Score / 获胜分数(血量)</span>
        </div>
        <div className="flex items-center gap-5 md:gap-6">
          <button 
            onClick={() => setHealth(Math.max(1, health - 1))}
            className="w-10 h-10 md:w-10 md:h-10 rounded-full bg-zinc-800 text-zinc-400 hover:bg-red-900/30 hover:text-red-400 flex items-center justify-center font-black text-xl transition-colors border border-zinc-700 hover:border-red-500/50"
          >
            -
          </button>
          <div className="text-4xl font-rajdhani font-bold text-white w-12 text-center">
            {health}
          </div>
          <button 
            onClick={() => setHealth(Math.min(10, health + 1))}
            className="w-10 h-10 md:w-10 md:h-10 rounded-full bg-zinc-800 text-zinc-400 hover:bg-green-900/30 hover:text-green-400 flex items-center justify-center font-black text-xl transition-colors border border-zinc-700 hover:border-green-500/50"
          >
            +
          </button>
        </div>
      </div>

      <div className="mb-4 md:mb-6 flex w-full max-w-3xl items-center gap-3 px-2">
        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-red-500/30 to-transparent"></div>
        <span className="text-[10px] font-orbitron uppercase tracking-[0.24em] text-red-200/60">Select Format / 选择模式</span>
        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-red-500/30 to-transparent"></div>
      </div>

      <div className="flex flex-col md:flex-row gap-4 md:gap-6 w-full max-w-4xl justify-center items-stretch">
        
        {/* Draw Battle */}
        <button 
          onClick={() => onSelectMode('DRAW', health)}
          className="group relative flex-1 flex flex-row md:flex-col items-center justify-start md:justify-center gap-4 md:gap-0 bg-black/40 border border-zinc-800 hover:border-orange-500 hover:bg-orange-900/10 rounded-2xl px-5 py-5 md:p-8 transition-all duration-300 backdrop-blur-sm overflow-hidden text-left md:text-center min-h-[168px] md:min-h-0"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/0 via-orange-500/0 to-orange-500/0 group-hover:from-orange-500/10 group-hover:via-transparent group-hover:to-transparent transition-all duration-500"></div>
          <Shuffle strokeWidth={1.5} className="w-14 h-14 md:w-20 md:h-20 text-zinc-600 group-hover:text-orange-500 transition-all duration-300 md:mb-6 group-hover:scale-110 shrink-0 relative z-10" />
          <div className="relative z-10 flex-1 flex flex-col items-start md:items-center">
            <h2 className="text-[28px] md:text-2xl font-orbitron font-bold text-white tracking-wide md:tracking-widest uppercase mb-1 md:mb-2 group-hover:text-orange-400 transition-colors">
              Draw Battle
            </h2>
            <h3 className="text-base md:text-lg font-rajdhani text-zinc-400 mb-2 md:mb-4 group-hover:text-orange-300 transition-colors">
              抽招赛对战
            </h3>
            <p data-i18n-skip="true" className="text-zinc-500 group-hover:text-orange-200/70 font-rajdhani text-xs md:text-sm text-left md:text-center max-w-xs transition-colors leading-snug">
              Select a custom trick deck and draw random tricks to battle against your opponent.
            </p>
          </div>
        </button>

        {/* Free Battle */}
        <button 
          onClick={() => onSelectMode('FREE', health)}
          className="group relative flex-1 flex flex-row md:flex-col items-center justify-start md:justify-center gap-4 md:gap-0 bg-black/40 border border-zinc-800 hover:border-red-500 hover:bg-red-900/10 rounded-2xl px-5 py-5 md:p-8 transition-all duration-300 backdrop-blur-sm overflow-hidden text-left md:text-center min-h-[168px] md:min-h-0"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-red-500/0 via-red-500/0 to-red-500/0 group-hover:from-red-500/10 group-hover:via-transparent group-hover:to-transparent transition-all duration-500"></div>
          <Swords strokeWidth={1.5} className="w-14 h-14 md:w-20 md:h-20 text-zinc-600 group-hover:text-red-500 transition-all duration-300 md:mb-6 group-hover:scale-110 shrink-0 relative z-10" />
          <div className="relative z-10 flex-1 flex flex-col items-start md:items-center">
            <h2 className="text-[28px] md:text-2xl font-orbitron font-bold text-white tracking-wide md:tracking-widest uppercase mb-1 md:mb-2 group-hover:text-red-400 transition-colors">
              Free Battle
            </h2>
            <h3 className="text-base md:text-lg font-rajdhani text-zinc-400 mb-2 md:mb-4 group-hover:text-red-300 transition-colors">
              自由对战
            </h3>
            <p data-i18n-skip="true" className="text-zinc-500 group-hover:text-red-200/70 font-rajdhani text-xs md:text-sm text-left md:text-center max-w-xs transition-colors leading-snug">
              Players choose their own tricks without drawing cards. Judges decide the outcome directly.
            </p>
          </div>
        </button>

      </div>
    </div>
  );
};
